import api from "./api";

export type EstadoVisita = "PENDIENTE" | "COMPLETADA" | "VENCIDA";
export type Prioridad = "ALTA" | "MEDIA" | "BAJA";

export interface IJustificacionVisita {
  causa: string;
  causa_id?: number | null;
  por?: string | null;
  por_nombre?: string | null;
  fecha?: string | null;
}

export interface IVisita {
  id: number;
  fiscal_id: number;
  punto_servicio_id: number;
  fecha: string;
  comentario?: string;
  cantidad_horas: number;
  cantidad_horas_hhmm?: string;
  prioridad: Prioridad;
  estado: EstadoVisita;
  origen: "AUTO" | "JEFATURA" | "N8N" | "SISTEMA" | string;
  creado_en?: string;
  actualizado_en?: string;
  ps_objeto?: string;
  ps_nombre?: string;
  // solo visitas VENCIDAS: fecha de la próxima Pendiente del mismo PS (reprogramación
  // automática del flujo diario) y justificación cargada (no modifica el registro)
  fecha_reprogramacion?: string | null;
  justificacion?: IJustificacionVisita | null;
  // solo COMPLETADA: cumplimiento por tiempo de permanencia (una visita está 100%
  // cumplida solo si el fiscal estuvo el tiempo planificado en el PS)
  minutos_permanencia?: number | null;
  minutos_acreditados?: number | null;
  pct_cumplimiento?: number | null;
  medicion_permanencia?: "OK" | "SIN_SALIDA" | "SIN_MARCACION" | null;
}

export interface IEditarVisitaPayload {
  id: number;
  fecha?: string;               // YYYY-MM-DD
  punto_servicio_id?: number;   
  ps_codigo?: string;           // alternativa al id
  prioridad?: Prioridad;        // "ALTA" | "MEDIA" | "BAJA"
  comentario?: string;
}


export async function editarVisita(payload: IEditarVisitaPayload): Promise<void> {
  await api.put(`/visitas_fiscales/editar`, payload);
}


export interface IListarVisitasParams {
  desde?: string;
  hasta?: string;
  fiscal_id?: number | string; // puede ser cédula/username
}
export interface IListarVisitasResponse {
  datos: IVisita[];
  rango: { desde: string; hasta: string };
}

export interface ICrearVisitaBody {
  fiscal_id: number | string;
  punto_servicio_id?: number;
  puntoServicio_id?: number;
  ps_codigo?: string;
  fecha: string;
  prioridad?: Prioridad;
  comentario?: string;
  creado_por_id?: number | string;
}

export interface IMoverVisitaResponse { ok: true; id: number; nueva_fecha: string; }
export interface ICambiarEstadoResponse { ok: true; id: number; estado: EstadoVisita; }
export interface IResumenResponse {
  fiscal_id: number;
  anio: number;
  mes: number;
  realizadas: number;
  total: number;
  texto: string;
  visitas?: { realizadas: number; meta: number; pct: number | null };
  horas?: {
    realizadas_min: number;
    meta_min: number;
    realizadas_hhmm: string;
    meta_hhmm: string;
    pct: number | null;
  };
  // promedio de % visitas y % horas
  cumplimiento?: { pct: number | null };
  ps_asignados?: number;
}
export interface IFiscalAsignado {
  cedula: string;
  nombre: string;
  operario_id?: number;
}

// helper local: header Authorization (sin tocar api.ts)
const authH = () => {
  const t =
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ===== Endpoints =====

export async function listarVisitas(params: IListarVisitasParams): Promise<IVisita[]> {
  const { data } = await api.get<IListarVisitasResponse>(
    "/visitas_fiscales/listar",
    { params, headers: { ...authH() } }
  );
  return data.datos;
}

export async function crearVisita(body: ICrearVisitaBody): Promise<{ ok: true }> {
  const { data } = await api.post<{ ok: true }>(
    "/visitas_fiscales/crear",
    body,
    { headers: { ...authH() } }
  );
  return data;
}

export async function moverVisita(id: number, fecha: string): Promise<IMoverVisitaResponse> {
  const { data } = await api.patch<IMoverVisitaResponse>(
    `/visitas_fiscales/${id}/mover`,
    { fecha },
    { headers: { ...authH() } }
  );
  return data;
}

export async function cambiarEstadoVisita(id: number, estado: EstadoVisita): Promise<ICambiarEstadoResponse> {
  const { data } = await api.patch<ICambiarEstadoResponse>(
    `/visitas_fiscales/${id}/estado`,
    { estado },
    { headers: { ...authH() } }
  );
  return data;
}

export interface IRecomendacionResponse {
  recomendacion: string;
  ia: boolean;
  total_visitas?: number;
  rango: { desde: string; hasta: string };
}

export async function recomendacionRuta(
  fiscal_id: number | string,
  desde: string,
  hasta: string
): Promise<IRecomendacionResponse> {
  const { data } = await api.get<IRecomendacionResponse>(
    "/visitas_fiscales/recomendacion",
    { params: { fiscal_id, desde, hasta }, headers: { ...authH() } }
  );
  return data;
}

export interface ICausaIncumplimiento {
  id: number;
  descripcion: string;
}

export async function listarCausasIncumplimiento(): Promise<ICausaIncumplimiento[]> {
  const { data } = await api.get<{ datos: ICausaIncumplimiento[] }>(
    "/visitas_fiscales/causas",
    { headers: { ...authH() } }
  );
  return data.datos || [];
}

export interface IJustificarResponse {
  ok: true;
  id: number;
  causa: string;
  causa_id?: number | null;
  fecha_reprogramacion?: string | null;
}

export async function justificarVisita(
  id: number,
  causa: string,
  causa_id?: number | null
): Promise<IJustificarResponse> {
  const { data } = await api.post<IJustificarResponse>(
    `/visitas_fiscales/${id}/justificar`,
    { causa, causa_id: causa_id ?? undefined },
    { headers: { ...authH() } }
  );
  return data;
}

export interface IAvancePS {
  ps_id: number;
  ps_nombre?: string;
  ps_codigo?: string;
  visitas: { realizadas: number; meta: number; faltan: number; pct: number | null };
  horas: { realizadas_hhmm: string; meta_hhmm: string; faltan_hhmm: string; pct: number | null };
  cumplimiento?: { pct: number | null };
  completo: boolean;
}
export interface IAvanceResponse {
  fiscal_id: number;
  anio: number;
  mes: number;
  totales: {
    ps_asignados: number;
    visitas: { realizadas: number; meta: number; pct: number | null };
    horas: { realizadas_hhmm: string; meta_hhmm: string; pct: number | null };
    cumplimiento?: { pct: number | null };
    ps_completos: number;
  };
  detalle: IAvancePS[];
}

export async function avanceFiscal(
  fiscal_id: number | string,
  anio: number,
  mes: number
): Promise<IAvanceResponse> {
  const { data } = await api.get<IAvanceResponse>(
    "/visitas_fiscales/avance",
    { params: { fiscal_id, anio, mes }, headers: { ...authH() } }
  );
  return data;
}

export async function resumenVisitas(fiscal_id: number | string, anio: number, mes: number): Promise<IResumenResponse> {
  const { data } = await api.get<IResumenResponse>(
    "/visitas_fiscales/resumen",
    { params: { fiscal_id, anio, mes }, headers: { ...authH() } }
  );
  return data;
}

const VisitasFiscalesService = {
  listarVisitas,
  crearVisita,
  moverVisita,
  cambiarEstadoVisita,
  resumenVisitas,
};
export default VisitasFiscalesService;


export interface IFiscalAsignado {
  cedula: string;
  nombre: string;
}

export const listarFiscalesAsignados = async (cedulaJefe?: string): Promise<IFiscalAsignado[]> => {
  const params = cedulaJefe ? { cedula_jefe: cedulaJefe } : undefined;
  const { data } = await api.get<{ datos: Array<{ cedula: string; nombre?: string; fiscal?: string }> }>(
    "visitas_fiscales/fiscales",
    { params, headers: { ...authH() } }
  );

  const unicos = new Map<string, IFiscalAsignado>();

  for (const item of data?.datos || []) {
    const cedula = (item.cedula ?? "").toString().trim();
    if (!cedula) continue;

    if (!unicos.has(cedula)) {
      unicos.set(cedula, {
        cedula,
        nombre: (item.nombre ?? item.fiscal ?? "").toString().trim(),
      });
    }
  }

  return Array.from(unicos.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
};
